import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, MessageCircle, RotateCcw, X } from 'lucide-react';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { useToast } from '@/hooks/useToast';
import { dinero, numero } from '@/lib/formato-tienda';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { cn } from '@/lib/cn';
import * as api from '@/api/seguimiento.api';
import type { EstadoDeSeguimiento, MotivoNoCompro, Seguimiento as Caso } from '@/api/seguimiento.api';

/**
 * SEGUIMIENTO DE VENTAS — quien quedó en comprar no se pierde.
 *
 * La vendedora ve los suyos; quien supervisa, todos. «Compró» se marca solo al confirmar la
 * venta de ese cliente; aquí se marca lo que no compró (con el motivo) o cuándo volver a
 * escribirle.
 */

const PESTANAS: Array<{ id: EstadoDeSeguimiento; etiqueta: string }> = [
  { id: 'PENDIENTE', etiqueta: 'Por cerrar' },
  { id: 'COMPRO', etiqueta: 'Compraron' },
  { id: 'NO_COMPRO', etiqueta: 'No compraron' },
];

const CUANDO = [
  { valor: 'toca', etiqueta: 'Toca escribirles' },
  { valor: 'viejos', etiqueta: 'Llevan 3 días o más' },
] as const;

const DIA = 24 * 60 * 60 * 1000;

function diasDesde(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DIA));
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
}

/** Mañana a las 9, como valor de un <input type="date">. */
function manana(): string {
  const d = new Date(Date.now() + DIA);
  return d.toISOString().slice(0, 10);
}

const inputCls =
  'w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900';

export default function Seguimiento() {
  // SUPERFILTRO (Josué, 14-oct): las pestañas de antes son la faceta «Estado» (su opción en
  // blanco es «Por cerrar», lo de por defecto); se suman vendedora, origen y cuándo. En la URL.
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { estado: 'PENDIENTE', sucursal: '', vendedora: '', origen: '', cuando: '', buscar: '' },
    { estado: PESTANAS.map((p) => p.id), origen: Object.keys(api.ORIGEN), cuando: CUANDO.map((c) => c.valor) },
  );
  const estado = filtros.estado as EstadoDeSeguimiento;
  const [cerrando, setCerrando] = useState<Caso | null>(null);
  const [posponiendo, setPosponiendo] = useState<Caso | null>(null);
  const [motivo, setMotivo] = useState<MotivoNoCompro>('no_contesto');
  const [nota, setNota] = useState('');
  const [fecha, setFecha] = useState(manana());
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['seguimiento', estado],
    queryFn: () => api.listar({ estado }),
    refetchInterval: 60_000,
  });
  const pendientes = useQuery({
    queryKey: ['seguimiento', 'PENDIENTE'],
    queryFn: () => api.listar({ estado: 'PENDIENTE' }),
    enabled: estado !== 'PENDIENTE',
  });

  const marcar = useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: api.Accion }) => api.marcar(id, accion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seguimiento'] });
      void qc.invalidateQueries({ queryKey: ['eficiencia'] });
      setCerrando(null);
      setPosponiendo(null);
      setNota('');
      toast.success('Seguimiento actualizado');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const casos = data ?? [];
  const abiertos = estado === 'PENDIENTE' ? casos : (pendientes.data ?? []);
  const vencidos = abiertos.filter((c) => c.volverEl && new Date(c.volverEl).getTime() < Date.now());
  const viejos = abiertos.filter((c) => diasDesde(c.creadoAt) >= 3);

  const q = filtros.buscar.trim().toLowerCase();
  const visibles = casos.filter(
    (c) =>
      (!q || `${c.cliente ?? ''} ${c.telefono ?? ''} ${c.anuncioTitulo ?? ''}`.toLowerCase().includes(q)) &&
      (!filtros.sucursal || (c.sucursal ?? '') === filtros.sucursal) &&
      (!filtros.vendedora || (c.vendedora ?? '') === filtros.vendedora) &&
      (!filtros.origen || c.origen === filtros.origen) &&
      (filtros.cuando !== 'toca' || (c.volverEl != null && new Date(c.volverEl).getTime() < Date.now())) &&
      (filtros.cuando !== 'viejos' || diasDesde(c.creadoAt) >= 3),
  );
  const facetas: Faceta[] = [
    { clave: 'estado', titulo: 'Estado', todas: 'Por cerrar', opciones: PESTANAS.filter((p) => p.id !== 'PENDIENTE').map((p) => ({ valor: p.id, etiqueta: p.etiqueta })) },
    // Sucursal (16-oct): la del canal por donde escribió, guardada en el caso.
    { clave: 'sucursal', titulo: 'Sucursal', opciones: opcionesDe(casos, (c) => c.sucursal) },
    { clave: 'vendedora', titulo: 'Vendedora', opciones: opcionesDe(casos, (c) => c.vendedora) },
    { clave: 'origen', titulo: 'Origen', opciones: opcionesDe(casos, (c) => c.origen, (o) => api.ORIGEN[o] ?? o) },
    { clave: 'cuando', titulo: 'Cuándo', opciones: CUANDO.map((c) => ({ ...c })) },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Seguimiento"
        subtitulo="Quien quedó en comprar: cuántos días lleva esperando, quién lo atiende y cómo terminó."
        kpis={
          <>
            <Kpi titulo="Por cerrar" valor={numero(abiertos.length)} pie="abiertos ahora" />
            <Kpi
              titulo="Toca escribirles"
              valor={numero(vencidos.length)}
              pie="ya pasó el día acordado"
              tono={vencidos.length > 0 ? 'malo' : 'normal'}
              onClick={() => ponerVarios({ estado: undefined, cuando: 'toca' })}
            />
            <Kpi
              titulo="Llevan 3 días o más"
              valor={numero(viejos.length)}
              pie="sin cerrar"
              onClick={() => ponerVarios({ estado: undefined, cuando: 'viejos' })}
            />
          </>
        }
      />

      <Superfiltro
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholder="Buscar por cliente, teléfono o anuncio…"
        facetas={facetas}
        valores={{ ...filtros, estado: estado === 'PENDIENTE' ? undefined : estado }}
        onCambiar={ponerVarios}
        onLimpiar={() => ponerVarios(sinFiltros(facetas))}
        resultados={isLoading ? undefined : textoDeResultados(visibles.length, casos.length, ['caso', 'casos'])}
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <div className="space-y-3 py-10 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudo cargar el seguimiento.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : casos.length === 0 ? (
        <EmptyState
          title={estado === 'PENDIENTE' ? 'Nadie esperando' : 'Todavía no hay casos aquí'}
          description={
            estado === 'PENDIENTE'
              ? 'Cuando alguien quede en comprar (el asistente lo pasa, lo marcas en el chat o queda una venta en borrador), sale aquí.'
              : 'Los casos cerrados del período salen aquí.'
          }
        />
      ) : visibles.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Nadie coincide con la búsqueda o los filtros.
        </p>
      ) : (
        <ul className="space-y-2">
          {visibles.map((c) => {
            const dias = diasDesde(c.creadoAt);
            const vencido = c.estado === 'PENDIENTE' && c.volverEl && new Date(c.volverEl).getTime() < Date.now();
            return (
              <li
                key={c.id}
                className={cn(
                  'rounded-lg border border-surface-border bg-surface p-3 sm:p-4',
                  vencido && 'border-red-300 dark:border-red-800',
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100">{c.cliente}</p>
                    <p className="text-xs text-slate-500">
                      {[c.vendedora ?? 'Sin asignar', c.sucursal, c.anuncioTitulo ? `Anuncio: ${c.anuncioTitulo}` : 'Sin anuncio']
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {c.estado === 'PENDIENTE' && (
                      <Badge variant={dias >= 3 ? 'warning' : 'default'}>
                        {dias === 0 ? 'Hoy' : dias === 1 ? '1 día' : `${dias} días`}
                      </Badge>
                    )}
                    {c.estado === 'COMPRO' && <Badge variant="success">Compró {dinero(c.montoVenta)}</Badge>}
                    {c.estado === 'NO_COMPRO' && (
                      <Badge variant="danger">{api.MOTIVOS.find((m) => m.id === c.motivo)?.etiqueta ?? 'No compró'}</Badge>
                    )}
                    {vencido && <Badge variant="danger">Toca escribirle</Badge>}
                  </div>
                </div>

                {(c.resumen || c.nota) && (
                  <p className="mt-2 break-words text-sm text-slate-600 dark:text-slate-300">{c.nota || c.resumen}</p>
                )}
                <p className="mt-1 text-xs text-slate-400">
                  {api.ORIGEN[c.origen] ?? c.origen} · desde el {fechaCorta(c.creadoAt)}
                  {c.volverEl && c.estado === 'PENDIENTE' ? ` · volver a escribirle el ${fechaCorta(c.volverEl)}` : ''}
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={`/conversations/${c.conversationId}`}
                    className="inline-flex h-[34px] items-center gap-1.5 rounded-lg border border-surface-border px-3.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> Abrir el chat
                  </Link>
                  {c.estado === 'PENDIENTE' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => { setPosponiendo(c); setNota(c.nota ?? ''); setFecha(c.volverEl ? c.volverEl.slice(0, 10) : manana()); }}>
                        <CalendarClock className="h-3.5 w-3.5" /> Volver a escribirle
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setCerrando(c); setNota(c.nota ?? ''); }}>
                        <X className="h-3.5 w-3.5" /> No compró
                      </Button>
                    </>
                  )}
                  {c.estado === 'NO_COMPRO' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={marcar.isPending}
                      onClick={() => marcar.mutate({ id: c.id, accion: { tipo: 'reabrir' } })}
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Reabrir
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-xs text-slate-400">
        <Check className="mr-1 inline h-3.5 w-3.5" />
        «Compró» se marca solo cuando se confirma una venta de ese cliente.
      </p>

      <Modal
        open={!!cerrando}
        onClose={() => setCerrando(null)}
        title="No compró"
        description={cerrando?.cliente}
        footer={
          <>
            <Button variant="outline" onClick={() => setCerrando(null)}>
              Cancelar
            </Button>
            <Button
              loading={marcar.isPending}
              onClick={() =>
                cerrando && marcar.mutate({ id: cerrando.id, accion: { tipo: 'no_compro', motivo, nota: nota || null } })
              }
            >
              Guardar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-1.5">
            {api.MOTIVOS.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm">
                <input type="radio" name="motivo" checked={motivo === m.id} onChange={() => setMotivo(m.id)} />
                {m.etiqueta}
              </label>
            ))}
          </div>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Algo más (opcional)"
            aria-label="Nota"
            className={inputCls}
          />
        </div>
      </Modal>

      <Modal
        open={!!posponiendo}
        onClose={() => setPosponiendo(null)}
        title="Volver a escribirle"
        description={posponiendo?.cliente}
        footer={
          <>
            <Button variant="outline" onClick={() => setPosponiendo(null)}>
              Cancelar
            </Button>
            <Button
              loading={marcar.isPending}
              onClick={() =>
                posponiendo &&
                marcar.mutate({
                  id: posponiendo.id,
                  accion: { tipo: 'volver', volverEl: new Date(`${fecha}T09:00:00`).toISOString(), nota: nota || null },
                })
              }
            >
              Guardar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-xs text-slate-500">¿Qué día?</span>
            <input type="date" value={fecha} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setFecha(e.target.value)} className={inputCls} />
          </label>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Qué quedó hablado (opcional)"
            aria-label="Nota"
            className={inputCls}
          />
        </div>
      </Modal>
    </div>
  );
}
